// DWLF-416: where the last close sits in the window's expected move (the API's `depth.reading.position`), as
// one label for the screener and the Cycle Clock. The placement is the API's (indicators `windowDepthBand`);
// nothing here re-reads the band. IN ZONE (the p25–p75 of past cycles' moves) is the loud one; NOT REACHED
// (short of the smallest move on record) is shown quiet, since nothing in the band applies yet.

// ONE vocabulary for the API's position enum, shared by the chart's zone label (`words`), the badges
// (`label`, `tone`) and their tooltips (`meaning`).
const POSITIONS = {
  'not-reached': { label: 'Not reached', tone: 'quiet', words: 'not reached yet', meaning: 'short of the smallest move on record' },
  shallow: { label: 'Shallow', tone: 'muted', words: 'shallow', meaning: 'past the smallest move, short of the typical range' },
  'in-zone': { label: 'IN ZONE', tone: 'loud', words: 'IN ZONE', meaning: 'inside the typical range (25th–75th percentile of past cycles)' },
  deep: { label: 'Deep', tone: 'muted', words: 'deep', meaning: 'past the typical range, inside the largest on record' },
  beyond: { label: 'Beyond', tone: 'muted', words: 'beyond the deepest on record', meaning: 'past the largest move on record' },
};

/** The API's positions, in this vocabulary's order (shallow → beyond, not-reached first). */
export const DEPTH_POSITIONS = Object.freeze(Object.keys(POSITIONS));

const known = (position) => Object.prototype.hasOwnProperty.call(POSITIONS, position ?? '');

/** The last close is inside the typical range: the one reading every surface makes loud. */
export const isInZone = (depth) => depth?.reading?.position === 'in-zone';

/** The badge label for a position (e.g. `IN ZONE`), or null for none or an unknown one. */
export const depthPositionLabel = (position) => (known(position) ? POSITIONS[position].label : null);

/** The tooltip meaning for a position, or null for none or an unknown one. */
export const depthPositionMeaning = (position) => (known(position) ? POSITIONS[position].meaning : null);

/** The chart's words for a position, or null for none or an unknown one. */
export const depthPositionWords = (position) => (known(position) ? POSITIONS[position].words : null);

/**
 * Only an anchor that says it is a confirmed pivot is worded as one (or a v1 anchor, which always was);
 * a running extreme (indicators 19) or anything else is not dressed as a pivot.
 */
export const isConfirmedAnchor = (a) => a?.source === 'confirmed-pivot' || (a?.source == null && Number.isFinite(a?.pivotTime));

/** The API's own label for the band, worded as the chart words it. */
export const depthClaim = (depth) => (depth?.verification?.state === 'context-only'
  ? 'historical context, not a target'
  : (depth?.verification?.state || 'unverified'));

const fmtPct = (v) => `${Math.abs(v).toFixed(1).replace(/\.0$/, '')}%`;
const fmtPrice = (v) => v.toLocaleString('en-GB', { maximumFractionDigits: v >= 100 ? 2 : 4 });

/**
 * The measured parts of a reading: how far the move has gone (null when withheld or absent), the typical range
 * (null without one) and the sample it rests on, with its caveats ("n 12, thin"). Shared by the tooltip sentence
 * and the briefing's IN ZONE rows.
 * @param {object|null} depth  a window's `depth` with a reading
 * @param {'low'|'high'} side  low windows measure a fall from the high, high windows a rise from the low
 */
export function depthMoveParts(depth, side) {
  const move = side === 'high' ? 'rise' : 'fall';
  const extreme = side === 'high' ? 'low' : 'high';
  const a = depth?.anchor;
  const from = Number.isFinite(a?.price)
    ? ` from the ${isConfirmedAnchor(a) ? `cycle ${extreme}` : `running ${extreme} (not yet confirmed)`} ${fmtPrice(a.price)}`
    : '';
  // `movePct` is withheld (null) from callers not licensed for raw candles; the bucket still shows.
  const moved = Number.isFinite(depth?.reading?.movePct)
    ? `${move === 'fall' ? 'Fallen' : 'Risen'} ${fmtPct(depth.reading.movePct)}${from}`
    : null;
  const typical = Number.isFinite(depth?.pct?.p25) && Number.isFinite(depth?.pct?.p75)
    ? `typical ${move} ${fmtPct(depth.pct.p25)}–${fmtPct(depth.pct.p75)}`
    : null;
  const sample = Number.isFinite(depth?.n)
    ? `n ${depth.n}${depth.thin ? ', thin' : ''}${depth.partialHistory ? ', partial history' : ''}`
    : null;
  return { moved, typical, sample };
}

/**
 * The reading of one window's `depth` for a list row, or null when there is none to show (no band, or no
 * reading: a past window, or an API without one).
 * @param {object|null} depth  the window's `depth` from GET /cycles/windows?depth=1 or a screener row
 * @param {'low'|'high'} side  low windows measure a fall from the high, high windows a rise from the low
 */
export function depthReading(depth, side) {
  const position = depth?.reading?.position;
  if (!known(position)) return null;
  const { moved, typical, sample } = depthMoveParts(depth, side);
  const parts = [];
  if (moved) parts.push(moved);
  parts.push(POSITIONS[position].meaning);
  if (typical) parts.push(sample ? `${typical} (${sample})` : typical);
  parts.push(depthClaim(depth));
  const { label, tone } = POSITIONS[position];
  return { position, label, tone, detail: parts.join(' · ') };
}
